import { ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { MatSelectionListChange } from '@angular/material/list';
import { Subject } from 'rxjs';
import { FacetedFilterFacetItemType } from '../../../../types/constants';
import { IFacetedFilterDataItem, ISelectionInfo, ISelectedFacetInfo } from '../../../../types/faceted-filter.types';
import { TypeCheck } from '../../../../Utils/type-check';

@Component({
  template: ``,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BasePaneComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<boolean>();

  isMultiSelect = false;

  selectedItem: IFacetedFilterDataItem | undefined;
  selectedCheckableItems: IFacetedFilterDataItem[] = [];

  itemsList: IFacetedFilterDataItem[] = [];
  checkableItemsList: IFacetedFilterDataItem[] = [];

  isIndeterminate = false;
  isSelectAllChecked = false;

  // Keep as regular properties for child class compatibility
  item!: IFacetedFilterDataItem;
  activeIndex!: number;

  // Must use EventEmitter (not output()) because these are subscribed to
  // programmatically via .pipe() in FilterPaneManagerService
  @Output() onItemSelected = new EventEmitter<ISelectionInfo>();
  @Output() onContainerItemSelected = new EventEmitter<IFacetedFilterDataItem>();
  @Output() onCheckableItemsChanged = new EventEmitter<ISelectionInfo>();
  @Output() onOperatorExpressionSelected = new EventEmitter<ISelectionInfo>();
  @Output() onDateRangeSelected = new EventEmitter<ISelectionInfo>();

  ngOnInit(): void {
    this.manageSelectionList();
    this.determineSelectMode();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  itemSelected($event: IFacetedFilterDataItem): void {
    this.selectedItem = $event;
    this.selectedCheckableItems = [];
    this.updateCheckboxState();
    const selection: ISelectedFacetInfo = {
      facets: [$event],
      item: this.item,
    };
    this.onItemSelected.emit(selection);
  }

  containerItemSelected($event: IFacetedFilterDataItem): void {
    this.selectedItem = $event;
    this.selectedCheckableItems = [];
    this.updateCheckboxState();
    this.onContainerItemSelected.emit($event);
  }

  checkableItemsChanged($event: MatSelectionListChange): void {
    if (this.isMultiSelect) {
      this.handleMultiSelectItems();
    } else {
      console.debug('base-pane selectedOption change');
      const selectedOption = $event.options[0]?.value as IFacetedFilterDataItem;
      if (selectedOption) {
        this.handleSingleSelectItem(selectedOption);
      }
    }
  }

  protected handleMultiSelectItems(): void {
    this.updateCheckboxState();
    const selection: ISelectedFacetInfo = {
      facets: [...this.selectedCheckableItems],
      item: this.item,
    };
    this.onCheckableItemsChanged.emit(selection);
  }

  protected handleSingleSelectItem(selectedOption: IFacetedFilterDataItem): void {
    this.selectedItem = undefined;
    this.selectedCheckableItems = [selectedOption];
    this.updateCheckboxState();

    const selection: ISelectedFacetInfo = {
      facets: [selectedOption],
      item: selectedOption,
    };
    if (selection) {
      this.onCheckableItemsChanged.emit(selection);
    } else {
      this.onContainerItemSelected.emit(selectedOption);
    }
  }

  protected updateCheckboxState(): void {
    const selectedCount = this.selectedCheckableItems?.length ?? 0;
    const totalCount = this.checkableItemsList.length;
    this.isSelectAllChecked = selectedCount > 0 && selectedCount === totalCount;
    this.isIndeterminate = selectedCount >= 1 && selectedCount !== totalCount;
  }

  selectAllCheckboxChanged($event: { checked: boolean }): void {
    this.selectedCheckableItems = [];
    if ($event.checked) {
      this.selectedCheckableItems = [...this.checkableItemsList];
    }
    this.updateCheckboxState();
    const selection: ISelectionInfo = {
      facets: [...this.selectedCheckableItems],
      item: this.item,
    };
    this.onCheckableItemsChanged.emit(selection);
  }

  protected determineSelectMode(): void {
    this.isMultiSelect =
      this.item && (this.item.items?.filter((i) => i.type !== FacetedFilterFacetItemType.Facet).length ?? 0) <= 0;
  }

  protected manageSelectionList(): void {
    if (this.item && this.item.items?.length) {
      this.checkableItemsList = this.item.items?.filter((i) => TypeCheck.isFacet(i.type));
      this.itemsList = this.item.items?.filter((i) => !TypeCheck.isFacet(i.type));
    }
  }
}
